import { PropsWithChildren } from 'react';
import { useAppContext } from '../../AppContext/AppContext';
import { CurrentUserSiteOptionsCreateContractFlow } from '../../graphql/generated';
import { useOfferContext } from '../../OfferContext/OfferContext';
import { ModuleName, OrderFlow } from '../../types';
import { moduleAvailable } from '../../utils/utils';
import SummaryCard from '../SummaryCard/SummaryCard';

const OfferSummaryTestId = ({ children }: PropsWithChildren) => (
  <div data-testid="offer-summary">{children}</div>
);

export const OfferSummary = () => {
  const { modules, user, selectedSite, orderFlow } = useAppContext();
  const { offer } = useOfferContext();
  const { energyDynamic, energyFix } = offer || {};

  const hasPriceOptimizationModule = modules.has(ModuleName.PRICE_OPTIMIZATION);
  const priceOptimizationAvailable = moduleAvailable(offer, ModuleName.PRICE_OPTIMIZATION);

  const energyFixCosts = energyFix?.configuration?.expectedPaymentGrossPerMonth;
  const energyDynamicWithModuleCosts =
    energyDynamic?.configurationWithBatteryOptimizationModule?.averagePrices
      .expectedPaymentGrossPerMonth;
  const energyDynamicWithoutModuleCosts =
    energyDynamic?.configurationWithoutBatteryOptimizationModule?.averagePrices
      .expectedPaymentGrossPerMonth;

  const configurationWithBatteryOptimizationModule =
    energyDynamic?.configurationWithBatteryOptimizationModule;

  if (orderFlow === OrderFlow.energyFix) {
    return (
      <OfferSummaryTestId>
        <SummaryCard dataTestId="summary-card-for-energy-fix" value={energyFixCosts} />
      </OfferSummaryTestId>
    );
  }

  if (
    !user ||
    !selectedSite ||
    selectedSite.options.createContractFlow ===
      CurrentUserSiteOptionsCreateContractFlow.SwitchFromSonnenStromContract
  ) {
    return (
      <OfferSummaryTestId>
        <SummaryCard
          variant="sonnenBattery"
          value={energyDynamicWithoutModuleCosts}
          dataTestId="summary-card-without-sonnenbattery"
        />
      </OfferSummaryTestId>
    );
  }

  const energyDynamicWithPriceOptimizationSummaryCard = (
    <SummaryCard
      dataTestId="summary-card-with-price-optimization"
      withPriceOptimization
      value={energyDynamicWithModuleCosts}
    />
  );

  const energyDynamicWithoutPriceOptimizationSummaryCard = (
    <SummaryCard
      value={energyDynamicWithoutModuleCosts}
      dataTestId="summary-card-without-price-optimization"
    />
  );

  return (
    <OfferSummaryTestId>
      {priceOptimizationAvailable &&
      hasPriceOptimizationModule &&
      configurationWithBatteryOptimizationModule
        ? energyDynamicWithPriceOptimizationSummaryCard
        : energyDynamicWithoutPriceOptimizationSummaryCard}
    </OfferSummaryTestId>
  );
};
